/**
 * <mini-slider> — a lightweight, reusable scroll-snap carousel.
 *
 * Markup contract (see snippets/mini-slider.liquid):
 *   <mini-slider>
 *     <div data-mini-slider-track>
 *       <div data-mini-slider-slide>…</div> …
 *     </div>
 *     <div data-mini-slider-controls>
 *       <ol> <li><button data-mini-slider-dot data-index="0"></button></li> … </ol>
 *       <button data-mini-slider-prev></button>
 *       <button data-mini-slider-next></button>
 *     </div>
 *   </mini-slider>
 *
 * The track is the horizontal scroll container on mobile. On desktop (>=1080px)
 * the consuming section may re-lay-out the track as a static list; this component
 * then stays dormant (no observer, controls are hidden via CSS).
 */
if (!customElements.get('mini-slider')) {
  class MiniSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mini-slider-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-mini-slider-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-mini-slider-dot]'));
      this.prevButton = this.querySelector('[data-mini-slider-prev]');
      this.nextButton = this.querySelector('[data-mini-slider-next]');
      this.activeIndex = 0;

      if (this.slides.length === 0) return;

      this.onPrev = () => this.goTo(this.activeIndex - 1);
      this.onNext = () => this.goTo(this.activeIndex + 1);
      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.desktopQuery = window.matchMedia('(min-width: 1080px)');
      this.onBreakpointChange = () => this.syncMode();
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      this.syncMode();
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
      this.observer?.disconnect();
    }

    /** Enable the carousel on mobile, keep it dormant on desktop. */
    syncMode() {
      if (this.desktopQuery.matches) {
        this.observer?.disconnect();
        this.observer = null;
        return;
      }

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = this.slides.indexOf(entry.target);
                if (index !== -1) this.setActive(index);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    }

    /** Scroll the track to the slide at `index` (clamped to range). */
    goTo(index) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[clamped];
      if (!slide) return;

      const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'smooth' });
      this.setActive(clamped);
    }

    /** Update the active dot and the disabled state of the arrows. */
    setActive(index) {
      this.activeIndex = index;

      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.classList.toggle('is-active', isActive);
      });

      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }
  }

  customElements.define('mini-slider', MiniSlider);
}
